import type { Locale } from "@/lib/i18n";
import { CATEGORIES, FAQ, type CategoryId, type FaqEntry } from "./faq";
import { CATEGORIES_HT, FAQ_HT } from "./faq.ht";
import { TUTORIALS, type Tutorial } from "./tutorials";
import { TUTORIALS_HT } from "./tutorials.ht";
import { PRIVACY, TERMS, type LegalSection } from "./legal";
import { PRIVACY_HT, TERMS_HT } from "./legal.ht";

/**
 * Locale-aware access to the public content.
 *
 * ENGLISH IS THE STRUCTURE, every other locale supplies STRINGS. The English modules own the ids,
 * the ordering, the categories and the step counts; a translation file is a `Record<id, strings>`
 * merged over that. Two properties follow, and both are the point:
 *
 *   · A translated page cannot drift out of order or lose an entry, because it never had its own
 *     array to drift.
 *   · A MISSING TRANSLATION FAILS THE BUILD. `pick()` throws rather than falling back, and these
 *     pages are statically generated, so `next build` stops instead of silently shipping an
 *     English answer under a Kreyòl heading. A half-translated page is worse than an untranslated
 *     one: the reader cannot tell which half they are looking at, and on a page that explains how
 *     money moves, that ambiguity is the dangerous kind.
 *
 * UI CHROME IS DIFFERENT and deliberately so — content/ui.ts `t()` falls BACK to English for a
 * missing key. A missing button label should degrade, not take the site down; a missing answer
 * about refunds should take the build down.
 */

const pick = <T>(
  table: Record<string, T>,
  id: string,
  what: string,
  locale: Locale,
): T => {
  const hit = table[id];
  if (!hit) {
    throw new Error(
      `[i18n] ${what} "${id}" has no ${locale} translation. Add it to content/${what}.${locale}.ts — ` +
        `English is the structural source, so every id in the English file must be translated.`,
    );
  }
  return hit;
};

export const getFaq = (locale: Locale): FaqEntry[] => {
  if (locale === "en") return FAQ;
  return FAQ.map((e) => {
    const tr = pick(FAQ_HT, e.id, "faq", locale);
    return { ...e, q: tr.q, a: tr.a };
  });
};

export const getCategories = (
  locale: Locale,
): { id: CategoryId; title: string; blurb: string }[] => {
  if (locale === "en") return CATEGORIES;
  return CATEGORIES.map((c) => {
    const tr = pick(CATEGORIES_HT, c.id, "faq-category", locale);
    return { ...c, title: tr.title, blurb: tr.blurb };
  });
};

export const getTutorials = (locale: Locale): Tutorial[] => {
  if (locale === "en") return TUTORIALS;
  return TUTORIALS.map((t) => {
    const tr = pick(TUTORIALS_HT, t.id, "tutorials", locale);
    // Step COUNT comes from English so the two languages cannot describe different procedures.
    // A translation with the wrong number of steps is a different tutorial, not a translated one.
    if (tr.steps.length !== t.steps.length) {
      throw new Error(
        `[i18n] tutorial "${t.id}" has ${t.steps.length} steps in English but ` +
          `${tr.steps.length} in ${locale}. They must describe the same procedure.`,
      );
    }
    return {
      ...t,
      title: tr.title,
      intro: tr.intro,
      prerequisites: tr.prerequisites,
      steps: tr.steps,
      note: tr.note ?? t.note,
    };
  });
};

const legal = (
  en: LegalSection[],
  table: Record<string, { heading: string; body: (string | string[])[] }>,
  what: string,
  locale: Locale,
): LegalSection[] => {
  if (locale === "en") return en;
  return en.map((s) => {
    const tr = pick(table, s.id, what, locale);
    // `emphasis` is carried from English on purpose: which clauses are highlighted (no refunds,
    // trading risk) is a legal decision, not a translation one.
    return { ...s, heading: tr.heading, body: tr.body };
  });
};

export const getTerms = (locale: Locale): LegalSection[] =>
  legal(TERMS, TERMS_HT, "legal-terms", locale);

export const getPrivacy = (locale: Locale): LegalSection[] =>
  legal(PRIVACY, PRIVACY_HT, "legal-privacy", locale);
